import { isEmpty } from 'lodash';
import {
  DISQUALIFICATION_USER_UNDERAGE,
  GENERIC_DISQUALIFICATION,
  PARTICIPATION_STATUS,
  QUALITY_DISQUALIFICATION,
  SANITY_DISQUALIFICATION,
  DISTRIBUTION_LINK_ENDPAGE_BUTTON_TYPES,
} from '../constants';
import Icon from './icons';
import { publicAssetsPrefix } from '../../../../configuration';

const trophyIcon = Icon({
  type: 'trophy',
  color: 'rgb(245, 212, 85)',
  size: 16,
});

class ScreenOutPage {
  constructor({
    reason = GENERIC_DISQUALIFICATION,
    reward = {},
    redirectOnDemand,
    endPageSettingType,
    onDemandEndPageUrl,
  }) {
    this.reason = reason;
    this.isPayableScreenout = !isEmpty(reward);
    this.reward = reward;
    this.redirectOnDemand = redirectOnDemand;
    this.endPageSettingType = endPageSettingType;
    this.onDemandEndPageUrl = onDemandEndPageUrl;
  }

  renderImage = (img) => {
    if (
      Pollfish.helper.hasCustomSurveyTheme() ||
      Pollfish.helper.whiteLabelEnabled()
    ) {
      return '';
    }
    return `<div class="loading__image-container">${img}</div>`;
  };

  renderContent = () => {
    let title;
    let subtitle;
    let img;

    switch (this.reason) {
      case PARTICIPATION_STATUS.SURVEY_EXPIRED:
      case PARTICIPATION_STATUS.SURVEY_REPLACED:
        title = Pollfish.ui.getText('offerwall.history.survey_expired');
        subtitle = Pollfish.ui.getText('offerwall.notifications.survey_closed');
        img = `<img src="${publicAssetsPrefix}/img/expired.png" alt="You have been screened out" class="loading__image e2e-expired">`;
        break;
      case PARTICIPATION_STATUS.DISQUALIFICATION_USER_UNDERAGE: {
        title = Pollfish.ui.getText('loading.title');
        subtitle = Pollfish.ui.getText('age.screen_out.subtitle');
        // we are guessing the age for underage disqualification since we don't have the value here
        const defaultAge = 16;
        subtitle = subtitle.replace('X', defaultAge);
        img = `<img src="${publicAssetsPrefix}/img/minors.png" alt="You have been screened out" class="loading__image">`;
        break;
      }
      case DISQUALIFICATION_USER_UNDERAGE: {
        title = Pollfish.ui.getText('loading.title');
        subtitle = Pollfish.ui.getText('age.screen_out.subtitle');
        img = `<img src="${publicAssetsPrefix}/img/minors.png" alt="You have been screened out" class="loading__image">`;
        break;
      }
      case PARTICIPATION_STATUS.VPN:
        title = Pollfish.ui.getText('screen_out.vpn.title');
        subtitle = Pollfish.ui.getText('screen_out.vpn.subtitle');
        img = `<img src="${publicAssetsPrefix}/img/vpn.png" alt="You have been screened out" class="loading__image">`;
        break;
      case PARTICIPATION_STATUS.SURVEY_ALREADY_TAKEN:
        title = Pollfish.ui.getText('loading.title');
        subtitle = Pollfish.ui.getText('screen_out.already_taken.subtitle');
        img = `<img src="${publicAssetsPrefix}/img/alreadytaken.png" alt="You have been screened out" class="loading__image">`;
        break;
      case PARTICIPATION_STATUS.SURVEY_NOT_ACTIVE:
        title = Pollfish.ui.getText('loading.title');
        subtitle = Pollfish.ui.getText('screen_out.not_active.subtitle');
        img = `<img src="${publicAssetsPrefix}/img/notactive.png" alt="You have been screened out" class="loading__image">`;
        break;
      case QUALITY_DISQUALIFICATION:
      case PARTICIPATION_STATUS.FINAL_QUALITY_SCREEN_OUT:
        title = Pollfish.ui.getText('offerwall.notifications.disqualified');
        subtitle = `<span class="error-circle">!</span><span class="block">${Pollfish.ui.getText(
          'offerwall.history.quality',
        )}</span>`;
        img = `<img src="${publicAssetsPrefix}/img/quality.png" alt="You have been screened out" class="loading__image">`;
        break;
      case PARTICIPATION_STATUS.TENTATIVE_QUALITY_SCREEN_OUT:
      case SANITY_DISQUALIFICATION:
        title = Pollfish.ui.getText('sanity.screen_out.title');
        subtitle = Pollfish.ui.getText('sanity.screen_out.subtitle');
        img = `<img src="${publicAssetsPrefix}/img/sleepy.png" alt="You have been screened out" class="loading__image">`;
        break;
      case PARTICIPATION_STATUS.USER_REJECTION:
      case PARTICIPATION_STATUS.SCREEN_OUT:
      default:
        title = Pollfish.ui.getText('loading.title');
        subtitle = Pollfish.ui.getText('screen_out.targeting.subtitle');
        img = `<img src="${publicAssetsPrefix}/img/cow.png" alt="You have been screened out" class="loading__image">`;
        break;
    }

    const hideImage =
      Pollfish.helper.hasCustomSurveyTheme() ||
      Pollfish.helper.whiteLabelEnabled();

    if (this.isPayableScreenout) {
      title = Pollfish.ui.getText('screen_out.payable.title');
      img = `<img src="${publicAssetsPrefix}/img/hybrid.png" alt="Partial reward" class="loading__image">`;

      return `
        <div class="loading__header ${
          hideImage ? 'loading__header--short' : ''
        }" data-testid="end-screen-text">
          <h1>${title}</h1>
          <p>${subtitle}</p>
        </div>
        ${this.renderImage(img)}
        <p class="margin-xs-btm flex flex-align-items--center flex-justify--center">
          <span class="line-height-reset">${trophyIcon}</span>
          <span class="margin-xs-left text-light">${Pollfish.ui.getText(
            'partial_reward',
          )}</span>
        </p>
        <p class="margin-xl-btm title--xl" data-testid="earned_reward">
          <span class="font-bold">${this.reward.value}</span>
          <span>${this.reward.name}</span>
        </p>
      `;
    }

    const header = `
        <h1>${title}</h1>
        <p>${subtitle}</p>
      `;

    return `
      <div class="loading__header ${
        hideImage ? 'loading__header--short' : ''
      }" data-testid="end-screen-text">${header}</div>
      ${this.renderImage(img)}
    `;
  };

  static renderSubmitBtn = (reason, endPageSettingType) => {
    let btnText = Pollfish.ui.getText('thank_you.non_incentive.btn_copy');

    if (Pollfish.helper.isDistributionLink()) {
      switch (endPageSettingType) {
        case DISTRIBUTION_LINK_ENDPAGE_BUTTON_TYPES.DEFAULT:
          btnText = Pollfish.ui.getText(
            'thank_you_screen.distribution_button_redirect',
          );
          break;
        case DISTRIBUTION_LINK_ENDPAGE_BUTTON_TYPES.RESULTS:
          btnText = Pollfish.ui.getText(
            'thank_you_screen.distribution_button_results',
          );
          break;
        default:
      }
    }

    const btn = `
        <a href="#" class="btn btn-primary btn-lg" data-btn="close-survey">
          ${btnText}
        </a>
      `;

    return `<div class="loading__submit-btn">${btn}</div>`;
  };

  static attachSubmitBtnEvent = (redirectOnDemand, onDemandEndPageUrl) => {
    $('#wrapper').on('tap', '[data-btn="close-survey"]', (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      // always return to the user_lang translation
      Pollfish.helper.resetTranslations();

      if (Pollfish.helper.isDistributionLink() && redirectOnDemand) {
        if (onDemandEndPageUrl) {
          window.location.href = onDemandEndPageUrl;
          return;
        }
      } else {
        Pollfish.mobile.interface.close();
      }

      $('#wrapper').off('tap', '[data-btn="close-survey"]');
    });
  };

  render() {
    const isDistributionLink = Pollfish.helper.isDistributionLink();
    let shouldHideSubmit =
      !isDistributionLink &&
      (Pollfish.helper.isPreview ||
        (!Pollfish.helper.isWithinWebview && Pollfish.helper.isHtmlOnly));

    if (
      isDistributionLink &&
      Pollfish.helper.isPreview &&
      !this.redirectOnDemand
    ) {
      shouldHideSubmit = false;
    } else if (isDistributionLink && !this.redirectOnDemand) {
      shouldHideSubmit = true;
    }

    const htmlTemplate = `
      <div class="main-content__home loading">
        ${this.renderContent()}
        ${
          shouldHideSubmit
            ? ''
            : ScreenOutPage.renderSubmitBtn(
                this.reason,
                this.endPageSettingType,
              )
        }
      </div>
      `;

    if (!shouldHideSubmit) {
      ScreenOutPage.attachSubmitBtnEvent(
        this.redirectOnDemand,
        this.onDemandEndPageUrl,
      );
    }

    return htmlTemplate;
  }
}

export default ScreenOutPage;
export const { renderSubmitBtn, attachSubmitBtnEvent } = ScreenOutPage;
